/**
 * The API's `benefitDisplayBlocks` decides which tabs show (2026-10-29, Ani); the rule derived here decides pre-selection
 * and is the visibility fallback for checks stored before the API sent the list.
 *
 * The benefit details show one tab per care-setting block with data, but only the relevant ones: the service line's own
 * block (`practiceTypeStatus.priorityBlock`), the blocks the quoted values came from, ASC Facility and Hospital Outpatient
 * together, and (in `PatientBenefits.tsx`) the two physician blocks always. This decides which blocks get a tab, as `details` keys.
 */

/** The `details` keys of the blocks, in the order the tab lists them. */
export const BENEFIT_BLOCK_ORDER = [
  "ASC_FacilitySummary",
  "HospitalOutPatientSummary",
  "HospitalInpatientSummary",
  "SpecialistOfficeSummary",
  "SurgicalSummary",
  "MRI_CT_ScanSummary",
  "Diagnostic_X_Ray",
  "DiagnosticMedicalSummary",
];

/** The two physician blocks: they always get their tab. */
export const PHYSICIAN_BENEFIT_BLOCKS = new Set([
  "SpecialistOfficeSummary",
  "SurgicalSummary",
]);

/** The engine's `provenance.source` → `details` key. PLAN_LEVEL and MEDICARE have no block. */
const BLOCK_BY_ENGINE_SOURCE = new Map<string, string>([
  ["ASC", "ASC_FacilitySummary"],
  ["HOSPITAL_OUTPATIENT", "HospitalOutPatientSummary"],
  ["HOSPITAL_INPATIENT", "HospitalInpatientSummary"],
  ["SPECIALIST_OFFICE", "SpecialistOfficeSummary"],
  ["SURGICAL", "SurgicalSummary"],
  ["MRI_CT", "MRI_CT_ScanSummary"],
  ["XRAY", "Diagnostic_X_Ray"],
  ["DIAGNOSTIC_MEDICAL", "DiagnosticMedicalSummary"],
]);

export const blockKeyForEngineSource = (source: unknown): string | undefined =>
  BLOCK_BY_ENGINE_SOURCE.get(source as string);

/** The API's summary field (`BENEFIT_BLOCK`) → `details` key. The X-ray block is shown as `Diagnostic_X_Ray`. */
const BLOCK_BY_SUMMARY_FIELD = new Map<string, string>([
  ...BENEFIT_BLOCK_ORDER.map((key): [string, string] => [key, key]),
  ["XRaySummary", "Diagnostic_X_Ray"],
]);

/** The `ServiceDetails.ServiceName` → `details` key (`BENEFIT_BLOCK_SERVICE_NAMES` in the API). */
const BLOCK_BY_SERVICE_NAME = new Map<string, string>([
  ["Ambulatory Service Center Facility", "ASC_FacilitySummary"],
  ["Hospital - Ambulatory Surgical", "ASC_FacilitySummary"],
  ["Hospital - Outpatient", "HospitalOutPatientSummary"],
  ["Hospital - Inpatient", "HospitalInpatientSummary"],
  ["Professional (Physician) Visit - Office", "SpecialistOfficeSummary"],
  ["Surgical", "SurgicalSummary"],
  ["MRI/CAT Scan", "MRI_CT_ScanSummary"],
  ["MRI/CT Scan", "MRI_CT_ScanSummary"],
  ["Diagnostic X-Ray", "Diagnostic_X_Ray"],
  ["Diagnostic Medical", "DiagnosticMedicalSummary"],
]);

const ENGINE_BENEFITS = [
  "FLAT_COPAY",
  "COINSURANCE_PERCENT",
  "PROFESSIONAL_COPAY",
];

const inOrder = (blocks: Set<string>) =>
  BENEFIT_BLOCK_ORDER.filter((key) => blocks.has(key));

/** ASC Facility and Hospital Outpatient always show together (Ani, 2026-10-28). */
const withPairedBlocks = (blocks: Set<string>) => {
  if (
    blocks.has("ASC_FacilitySummary") ||
    blocks.has("HospitalOutPatientSummary")
  ) {
    blocks.add("ASC_FacilitySummary");
    blocks.add("HospitalOutPatientSummary");
  }
  return blocks;
};

/** The API's `benefitDisplayBlocks` as `details` keys, facility then physician, in its order; null when absent. */
export const apiDisplayBlocks = (response: any): string[] | null => {
  const lists = response?.benefitDisplayBlocks;
  if (!Array.isArray(lists?.facility)) return null;
  const physician = Array.isArray(lists.physician) ? lists.physician : [];
  const blocks = [...lists.facility, ...physician]
    .map((name: string) => BLOCK_BY_SUMMARY_FIELD.get(name))
    .filter((key): key is string => Boolean(key));
  return Array.from(new Set(blocks));
};

/**
 * The blocks a quoted value came from, as derived from the response's own sources — what pre-selection trusts; never
 * the API list. "ALL" for a response stored before the API said where its values came
 * from (no `practiceTypeStatus`, `engineBenefitSelections`, `coinsuranceSource`, `copaySource` or
 * `professionalCopaySource`). Legacy adds the blocks named by all three sources; a legacy response with
 * none of them whose priority block came back empty is also "ALL".
 */
export function sourceBenefitBlocks(response: any): string[] | "ALL" {
  const engine = response?.engineBenefitSelections;
  const source = response?.coinsuranceSource;
  const copaySource = response?.copaySource;
  const professionalCopaySource = response?.professionalCopaySource;
  const blocks = new Set<string>();
  const add = (block: string | undefined) => {
    if (block) blocks.add(block);
  };
  const addSource = (from: any) => {
    if (from?.type === "ServiceDetails") {
      add(BLOCK_BY_SERVICE_NAME.get(from.serviceName));
    }
    if (from?.type === "Summary") {
      add(BLOCK_BY_SUMMARY_FIELD.get(from.summaryField));
    }
  };

  add(BLOCK_BY_SUMMARY_FIELD.get(response?.practiceTypeStatus?.priorityBlock));

  if (engine?.authoritativeSide === "ENGINE") {
    ENGINE_BENEFITS.forEach((benefit) => {
      const selection = engine.benefits?.[benefit];
      if (selection?.quoted === true) {
        add(BLOCK_BY_ENGINE_SOURCE.get(selection.provenance?.source));
      }
    });
    return inOrder(withPairedBlocks(blocks));
  }

  if (
    !response?.practiceTypeStatus &&
    !engine &&
    !source &&
    !copaySource &&
    !professionalCopaySource
  )
    return "ALL";

  // Stored before the API recorded copay sources: the main block was empty and nothing says where the copay came from.
  if (
    !source &&
    !copaySource &&
    !professionalCopaySource &&
    response?.practiceTypeStatus?.isPriorityBlockUnanswered === true
  )
    return "ALL";

  addSource(source);
  addSource(copaySource);
  addSource(professionalCopaySource);
  return inOrder(withPairedBlocks(blocks));
}

/** The tabs to show: the API's list when present, else the derived rule. */
export function visibleBenefitBlocks(response: any): string[] | "ALL" {
  return apiDisplayBlocks(response) ?? sourceBenefitBlocks(response);
}

/** The `details` key of the block the quoted flat copay was read from: the engine's quoted `FLAT_COPAY`, else legacy's `copaySource`. */
export const flatCopaySourceBlock = (response: any): string | undefined => {
  const engine = response?.engineBenefitSelections;
  if (engine?.authoritativeSide === "ENGINE") {
    const selection = engine.benefits?.FLAT_COPAY;
    return selection?.quoted === true
      ? BLOCK_BY_ENGINE_SOURCE.get(selection.provenance?.source)
      : undefined;
  }
  const from = response?.copaySource;
  if (from?.type === "ServiceDetails")
    return BLOCK_BY_SERVICE_NAME.get(from.serviceName);
  if (from?.type === "Summary")
    return BLOCK_BY_SUMMARY_FIELD.get(from.summaryField);
  return undefined;
};
